import { useMemo } from 'react';

import { JsonObject } from '@suntex.core/types';
import { Entities_Modules_Module } from '@suntex.fim/api-client';

import { useFramework } from './useFramework';
import { useSessionDataLoader } from './useSessionDataLoader';

const DIRECTIONS_BASE = 'https://explore.firstinmath.com/directions';

const AREA_PERIMETER_SLUGS = new Set([
  'area-perimeter-square',
  'area-perimeter-circle',
  'area-perimeter-rectangle',
  'area-perimeter-triangle',
]);

export type ModuleWithDirections = Entities_Modules_Module & {
  directionsUrl?: string;
};

export function getDirectionsUrl(moduleSlug: string): string | undefined {
  if (moduleSlug === 'wdyk2') {
    return undefined;
  }

  if (moduleSlug.startsWith('vif-') && moduleSlug !== 'vif-tiles') {
    return undefined;
  }

  if (moduleSlug.startsWith('gym-')) {
    return `${DIRECTIONS_BASE}/gyms/all-gym`;
  }

  if (AREA_PERIMETER_SLUGS.has(moduleSlug)) {
    return `${DIRECTIONS_BASE}/games/area-perimeter`;
  }

  if (moduleSlug === 'ss-2-1') {
    return `${DIRECTIONS_BASE}/skillsets/2-1`;
  }

  if (moduleSlug.startsWith('ss-')) {
    return `${DIRECTIONS_BASE}/skillsets/${moduleSlug}`;
  }

  if (moduleSlug.startsWith('jtf-')) {
    return `${DIRECTIONS_BASE}/facts/jtf-10x10`;
  }

  const knowAndShow = /^ks-(\d+)-/.exec(moduleSlug);
  if (knowAndShow?.[1]) {
    const k2MaxLevel = 2;
    const page =
      Number(knowAndShow[1]) > k2MaxLevel
        ? 'know-and-show'
        : 'know-and-show-12';
    return `${DIRECTIONS_BASE}/word-problems/${page}`;
  }

  return `${DIRECTIONS_BASE}/games/${moduleSlug}`;
}

function withDirectionsUrl(
  module: Entities_Modules_Module,
): ModuleWithDirections {
  const directionsUrl = getDirectionsUrl(module.slug);
  return {
    ...module,
    ...(directionsUrl ? { directionsUrl } : {}),
  };
}

export function useModules(): ModuleWithDirections[] | undefined {
  const { api } = useFramework();
  const modules =
    useSessionDataLoader<
      { modules?: Entities_Modules_Module[] } & JsonObject,
      'modules'
    >('modules', async () => {
      const responseModules = await api.get_Modules_All({});
      return responseModules.modules;
    }).data ?? undefined;

  return useMemo(() => modules?.map(withDirectionsUrl), [modules]);
}

export function useModuleById(
  moduleId: number,
): ModuleWithDirections | undefined {
  const modules = useModules();
  return modules?.find((x) => x.id === moduleId);
}

export function useModuleBySlug(slug: string): ModuleWithDirections | null {
  const modules = useModules();
  return modules?.find((x) => x.slug === slug) ?? null;
}
